import { UseFormReturn } from "react-hook-form";
import { Box, Button, Grid } from "@mui/material";
import {
    AvailabilityPreferences,
    AvailabilitySlot,
    PriorityData,
    TechnicianPriority,
    WeeklyAvailabilityFromApi,
} from "../../utils/types";
import { useMemo } from "react";
import { ClinicsDropdown } from "../../../general-dropdowns/clinics-dropdown/ClinicsDropdown";
import { useStore } from "../../../../store/store";
import { getWeeksSlots } from "../../../../api/availabilities-finder";
import { getFieldsFromParams, getValueByEntity } from "../../../../api/useItemsFromSearchParams";
import { useSearchParams } from "react-router-dom";
import { PreferredDays } from "./PreferredDays";
import { ScreenSearchDesktop } from "@mui/icons-material";
import { findAvailabilityFormValidations } from "../../utils/formsValidations";
import { FilterPsychologists } from "./FilterPsychologists";
import { getRecords } from "../../../../api/useRecords";
import { ChoseStartDate } from "./ChoseStartDate";
import { PeriodOfDay } from "./PeriodOfDay";
import { ConsecutiveSessions } from "./ConsecutiveSessions";
import { LeadOrTratmentSelect } from "./LeadOrTratmentSelect";
import { ServicesSelection } from "./ServicesSelection";
import { useAvailabilityFormLogic } from "../../utils/useAvailabilityFormLogic";

type AvailabilityFormFinderProps = {
    entityForm: UseFormReturn<AvailabilityPreferences>;
    setSelectedSlots: (slots: AvailabilitySlot[]) => void;
    setWeeks: (weeks: WeeklyAvailabilityFromApi[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    selectedSlots: AvailabilitySlot[];
    isLoading: boolean;
    setPreferences: (preferences: AvailabilityPreferences) => void;
    setPrioritiesData: (data: PriorityData | undefined) => void;
};

export const AvailabilityFormFinder = ({
    entityForm,
    setIsLoading,
    selectedSlots,
    setSelectedSlots,
    isLoading,
    setWeeks,
    setPreferences,
    setPrioritiesData,
}: AvailabilityFormFinderProps) => {
    const serviceData = useAvailabilityFormLogic(entityForm);
    const {
        selectedServiceFirst,
        selectedServiceSecond,
        psychologistTreatment,
        firstServiceIsPsy,
        secondServiceIsPsy,
    } = serviceData;
    const { notifications } = useStore();
    const [params] = useSearchParams();
    const allFields = useMemo(() => getFieldsFromParams(params), [params]);
    const formValues = entityForm.getValues();
    const formClinicId = entityForm.watch("clinic_id");
    const formTreatClientClinicId = formValues?.treatment?.client?.clinic_id
    const formLeadClinicId = formValues?.lead?.clinic?.id

    const handleFormSubmit = async (data: AvailabilityPreferences) => {
        if (formClinicId) {
            if (formTreatClientClinicId && formTreatClientClinicId !== formClinicId) {
                const confirm = window.confirm("O cliente está associado a uma clínica diferente da clínica selecionada. Deseja continuar mesmo assim?");
                if (!confirm) {
                    return;
                }
            }
            if (formLeadClinicId && formLeadClinicId !== formClinicId) {
                const confirm = window.confirm("A lead está associada a uma clínica diferente da clínica selecionada. Deseja continuar mesmo assim?");
                if (!confirm) {
                    return;
                }
            }
        }
        setIsLoading(true);

        if (!findAvailabilityFormValidations(data, notifications)) {
            setIsLoading(false);
            entityForm.reset(data, {
                keepValues: true,
            });
            return;
        }

        if (data.age_groups?.id && data.disorder_specialization?.id) {
            const techniciansPriorities = await getRecords("technician_priorities", "*.*,technician.user_id.*", {
                extraOptions: { pageSize: 1000 },
                staleTime: Infinity,
            });
            const prioritiesData = {
                priorities: techniciansPriorities.items as TechnicianPriority[],
                filters: {
                    age_groups: data.age_groups?.id,
                    disorder_specialization: data.disorder_specialization?.id,
                },
            };
            setPrioritiesData(prioritiesData);
        }

        setSelectedSlots([]);
        const weeks = await getWeeksSlots(data, []);

        setWeeks(weeks);

        setPreferences(data);
        setIsLoading(false);
        entityForm.reset(data, {
            keepValues: true,
        });
    };
    
    return (
        <form onSubmit={entityForm.handleSubmit(handleFormSubmit)}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <ClinicsDropdown
                        fieldName="clinic_id"
                        value={getValueByEntity(allFields, 'clinics')}
                        entityForm={entityForm as unknown as UseFormReturn}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ChoseStartDate entityForm={entityForm} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <PeriodOfDay entityForm={entityForm} />
                </Grid>
            </Grid>
            <Grid container spacing={3} p={2}>
                <Grid item xs={12} md={10}>
                    <PreferredDays entityForm={entityForm as unknown as UseFormReturn} />
                </Grid>
                <Grid item xs={12} md={2} textAlign="right">
                    <ConsecutiveSessions entityForm={entityForm} setSelectedSlots={setSelectedSlots} />
                </Grid>
            </Grid>
            <Grid container spacing={3} p={2}>
                <Grid item xs={12} md={4} display="flex" flexDirection="column" gap={2}>
                    <LeadOrTratmentSelect
                        itemFromParams={allFields}
                        fieldName="leadOrTreatment"
                        entityForm={entityForm as unknown as UseFormReturn}
                    />
                    {(firstServiceIsPsy || secondServiceIsPsy) && (selectedServiceFirst || selectedServiceSecond) ? (
                        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                            <FilterPsychologists
                                entityForm={entityForm as unknown as UseFormReturn}
                                disabled={psychologistTreatment ? true : false}
                            />
                        </Box>
                    ) : null}
                </Grid>
                <ServicesSelection entityForm={entityForm} serviceData={serviceData} />
            </Grid>

            <Grid container spacing={3} mt={2}>
                <Grid item xs={12}>
                    <Button startIcon={<ScreenSearchDesktop />} type="submit" variant="contained" color="secondary" fullWidth>
                        {isLoading ? "A procurar..." : "Procurar disponibilidades"}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};
